import { useState } from "react";
import { CalendarClock, X } from "lucide-react";
import type { Content } from "./types";

/** `YYYY-MM-DDTHH:mm` local para <input type="datetime-local">. */
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Mañana a las 10:00 (hora del navegador). */
function tomorrowAtTen(): Date {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  return d;
}

/**
 * Programar una pieza: queda publicada pero el público la ve desde la fecha
 * elegida (hora del navegador; en Chile, hora de Chile). Hasta un año.
 */
export function ScheduleDialog({
  item,
  busy,
  onClose,
  onConfirm,
}: {
  item: Content;
  busy: boolean;
  onClose: () => void;
  onConfirm: (publishAtIso: string) => void;
}) {
  const initial =
    item.publishedAt && new Date(item.publishedAt).getTime() > Date.now() ? new Date(item.publishedAt) : tomorrowAtTen();
  const [value, setValue] = useState(toLocalInput(initial));
  const date = new Date(value);
  const valid = !Number.isNaN(date.getTime()) && date.getTime() > Date.now() + 60_000;
  const tooFar = valid && date.getTime() - Date.now() > 366 * 24 * 60 * 60 * 1000;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md rounded-lg border border-border-subtle bg-bg-base shadow-xl">
        <div className="flex items-center justify-between border-b border-border-subtle px-5 py-3">
          <h2 className="flex items-center gap-2 text-base font-semibold text-fg">
            <CalendarClock size={16} /> Programar publicación
          </h2>
          <button onClick={onClose} className="rounded p-1.5 text-fg-muted hover:bg-bg-subtle hover:text-fg">
            <X size={18} />
          </button>
        </div>
        <div className="flex flex-col gap-3 px-5 py-4">
          <p className="text-sm text-fg-muted">
            «{item.title}» aparecerá en la app y en mixcore.club/comunidad desde esta fecha. Hasta entonces sólo se ve aquí.
          </p>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-fg-muted">Fecha y hora</span>
            <input
              type="datetime-local"
              className="nx-input"
              value={value}
              min={toLocalInput(new Date())}
              onChange={(e) => setValue(e.target.value)}
            />
          </label>
          {!valid && <p className="text-xs text-danger">Elige una fecha futura.</p>}
          {tooFar && <p className="text-xs text-danger">Se puede programar hasta un año hacia adelante.</p>}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-border-subtle px-5 py-3">
          <button onClick={onClose} className="nx-btn-secondary">
            Cancelar
          </button>
          <button
            onClick={() => onConfirm(date.toISOString())}
            disabled={!valid || tooFar || busy}
            className="nx-btn-primary"
          >
            {busy ? "Programando…" : "Programar"}
          </button>
        </div>
      </div>
    </div>
  );
}
